<!--
  Guest login modal content (logo, email/password, Google).
  Shared component so desktop (UiModal) and mobile (fullscreen overlay) use
  identical content layout.

  Google login: GuestGoogleLoginButton → useGoogleIdentity → loginWithGoogle().
  Success signalled globally via googleLoginSuccessAt, modal closes on that.

  Email/password flows (same endpoint, register flag decides). A segmented
  tab control at the top switches between the two intents:
    - Tab "Einloggen" (mode='login', 2 fields): login only — never creates an
      account (a typo in the email would otherwise register a ghost account).
    - Tab "Registrieren" (mode='register', 3 fields: email + password twice):
      explicit sign-up (register=true) → mode='confirm' (code from email) →
      auto-login after confirmation.
    - "Forgot password?" → reset (mode='reset') → reset-confirm (code + new password).
      Unconfirmed accounts are routed to mode='confirm' first (Cognito cannot
      send reset codes to unverified emails).
-->
<template>
  <div class="flex flex-col items-center text-center w-full max-w-[400px] mx-auto">
    <UiIcon :src="links?.logo" width="150" height="36" alt="Logo" class="h-9 w-auto mb-6" />

    <!-- Intent switch: login (2 fields) vs. register (email + password twice).
         Only shown in the two main modes; code/reset steps have a back button. -->
    <div
      v-if="mode === 'login' || mode === 'register'"
      class="w-full grid grid-cols-2 gap-1 rounded-xl bg-neutral-100 p-1 mb-5"
      role="tablist"
      aria-label="Einloggen oder registrieren"
    >
      <button
        type="button"
        role="tab"
        :aria-selected="mode === 'login'"
        class="rounded-lg py-2 text-sm font-semibold transition-colors"
        :class="mode === 'login' ? 'bg-white text-petrol-600 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'"
        @click="switchMode('login')"
      >
        <UiLanguagePlaceholder domain="GuestLoginForm" name="tab_login" />
      </button>
      <button
        type="button"
        role="tab"
        :aria-selected="mode === 'register'"
        class="rounded-lg py-2 text-sm font-semibold transition-colors"
        :class="mode === 'register' ? 'bg-white text-petrol-600 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'"
        @click="switchMode('register')"
      >
        <UiLanguagePlaceholder domain="GuestLoginForm" name="tab_register" />
      </button>
    </div>

    <form class="w-full text-left" @submit.prevent="onSubmit">
      <div class="space-y-3">
        <!-- Password reset: short explanation of what happens next -->
        <p v-if="mode === 'reset'" class="text-sm text-neutral-600">
          <UiLanguagePlaceholder domain="GuestLoginForm" name="reset_intro" />
        </p>

        <!-- Email: editable in all modes except code confirmation -->
        <UiInput
          v-if="mode !== 'confirm'"
          v-model="email"
          type="email"
          name="email"
          autocomplete="username"
          label-position="top"
          placeholder="name@beispiel.de"
        >
          <template #label><UiLanguagePlaceholder domain="GuestLoginForm" name="email_label" /></template>
        </UiInput>

        <!-- Code confirmation after registration -->
        <template v-if="mode === 'confirm'">
          <p class="text-sm text-neutral-600">
            <UiLanguagePlaceholder domain="GuestLoginForm" name="confirm_intro" :variables="{ email }" />
          </p>
          <UiInput v-model="code" type="text" name="code" autocomplete="one-time-code" inputmode="numeric" label-position="top" placeholder="123456">
            <template #label><UiLanguagePlaceholder domain="GuestLoginForm" name="code_label" /></template>
          </UiInput>
          <div class="flex justify-end">
            <button
              type="button"
              class="text-sm font-semibold text-petrol-500 hover:underline disabled:opacity-50"
              :disabled="loading"
              @click="onResendCode"
            >
              <UiLanguagePlaceholder domain="GuestLoginForm" name="resend_code" />
            </button>
          </div>
        </template>

    <!-- Complete password reset: code from email. The Google hint keeps
         Google-only accounts out of a dead end (they have no password to
         reset); shown to everyone so it reveals nothing about the account. -->
    <p v-if="mode === 'reset-confirm'" class="text-sm text-neutral-600">
      <UiLanguagePlaceholder domain="GuestLoginForm" name="reset_confirm_intro" :variables="{ email }" />
    </p>
        <UiInput
          v-if="mode === 'reset-confirm'"
          v-model="code"
          type="text"
          name="code"
          autocomplete="one-time-code"
          inputmode="numeric"
          label-position="top"
          placeholder="123456"
        >
          <template #label><UiLanguagePlaceholder domain="GuestLoginForm" name="reset_code_label" /></template>
        </UiInput>

        <!-- Password field for login, registration and password reset -->
        <UiInput
          v-if="mode === 'login' || mode === 'register' || mode === 'reset-confirm'"
          v-model="password"
          type="password"
          :name="mode === 'login' ? 'password' : 'new-password'"
          :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
          label-position="top"
        >
          <template #label>{{ passwordLabel }}</template>
        </UiInput>

        <!-- Registration: confirm the chosen password (replaces the name field) -->
        <UiInput
          v-if="mode === 'register'"
          v-model="passwordConfirm"
          type="password"
          name="new-password-confirm"
          autocomplete="new-password"
          label-position="top"
        >
          <template #label><UiLanguagePlaceholder domain="GuestLoginForm" name="password_confirm_label" /></template>
        </UiInput>

        <!-- Live checklist for both password fields (match rule included on register) -->
        <ul v-if="showPasswordRules" class="mt-2 space-y-1">
          <li
            v-for="rule in displayedPasswordRules"
            :key="rule.label"
            class="flex items-center gap-x-1.5 text-xs font-light"
            :class="rule.valid ? 'text-petrol-500' : 'text-neutral-400'"
          >
            <UiIcon
              v-if="rule.valid"
              src="/svg/guest/check.svg"
              width="14"
              height="14"
              class="shrink-0 stroke-petrol-500"
            />
            <span v-else class="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            {{ rule.label }}
          </li>
        </ul>

        <div v-if="mode === 'login'" class="flex justify-end">
          <button
            type="button"
            class="text-sm font-semibold text-petrol-500 hover:underline"
            @click="switchMode('reset')"
          >
            <UiLanguagePlaceholder domain="GuestLoginForm" name="forgot_password" />
          </button>
        </div>
      </div>

      <!-- Feedback messages. Errors use cta-700 (portal red): theme.css
           wipes Tailwind's default `red` palette (`--color-*: initial`). -->
      <p v-if="errorMsg" class="mt-3 text-sm text-cta-700" role="alert">{{ errorMsg }}</p>
      <p v-else-if="infoMsg" class="mt-3 text-sm text-green-700">{{ infoMsg }}</p>

      <button
        type="submit"
        :disabled="loading"
        class="w-full rounded-xl bg-petrol-500 py-3 px-4 mt-4 font-semibold text-white transition-colors hover:bg-petrol-600 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {{ loading ? 'Bitte warten …' : submitLabel }}
      </button>
    </form>

    <!-- Login/register tabs: Google only on GCP-allowlisted portals. -->
    <template v-if="mode === 'login' || mode === 'register'">
      <template v-if="googleLoginEnabled">
        <div class="w-full flex items-center gap-x-3 my-5">
          <span class="h-px bg-neutral-200 flex-1" />
          <span class="text-sm text-neutral-500"><UiLanguagePlaceholder domain="GuestLoginForm" name="or" /></span>
          <span class="h-px bg-neutral-200 flex-1" />
        </div>

        <!-- Real Google login: renders the Google button. Login flows centrally
             through the guest store; success → googleLoginSuccessAt (modal navigates),
             errors → watch on googleAuthError (below). -->
        <component :is="GoogleLoginButton" v-if="GoogleLoginButton" class="w-full flex justify-center" />
      </template>

      <!-- Privacy note (TR-40172): one placeholder for the whole paragraph,
           links included in the placeholder value (UiLanguagePlaceholder
           renders HTML). Also shown when Google is hidden (email-only). -->
      <p class="mt-5 text-xs text-neutral-500 text-center">
        <UiLanguagePlaceholder domain="GuestLoginForm" name="privacy_note" />
      </p>
    </template>

    <!-- Back to login (code confirmation and reset steps) -->
    <button
      v-else
      type="button"
      class="mt-5 inline-flex items-center gap-x-1 text-sm font-semibold text-petrol-500 hover:underline"
      @click="switchMode('login')"
    >
      <UiIcon src="/svg/guest/chevron-left.svg" width="16" height="16" class="stroke-petrol-500" />
      <UiLanguagePlaceholder domain="GuestLoginForm" name="back_to_login" />
    </button>
  </div>
</template>

<script setup lang="ts">
type Mode = 'login' | 'register' | 'confirm' | 'reset' | 'reset-confirm'

const emit = defineEmits<{(e: 'success'): void}>()

const { links } = usePortalData()
const { loginWithCognito, confirmSignUp, resendSignUpConfirmationCode, sendPasswordResetCode, confirmPasswordReset } = useGuest()
const { googleAuthError } = useGoogleIdentity()
const { log } = useGuestActivityLog()
const { initialMode } = useGuestLoginModal()
const googleLoginEnabled = useGoogleLoginEnabled()
const GoogleLoginButton = googleLoginEnabled
  ? defineAsyncComponent(() => import('~/components/page-sections/guest/GuestGoogleLoginButton.vue'))
  : null

const mode = ref<Mode>(initialMode.value === 'register' ? 'register' : 'login')
const email = ref('')
const password = ref('')
const passwordConfirm = ref('')
const code = ref('')
const loading = ref(false)
const errorMsg = ref('')
const infoMsg = ref('')

// Live password rule validation (matches Cognito policy).
const passwordRules = computed(() => [
  { label: 'Mindestens 8 Zeichen', valid: password.value.length >= 8 },
  { label: 'Ein Großbuchstabe', valid: /[A-Z]/.test(password.value) },
  { label: 'Ein Kleinbuchstabe', valid: /[a-z]/.test(password.value) },
  { label: 'Eine Zahl', valid: /[0-9]/.test(password.value) }
])
const passwordValid = computed(() => passwordRules.value.every(rule => rule.valid))
const passwordsMatch = computed(() => password.value !== '' && password.value === passwordConfirm.value)

// Registration shows the match check as part of the live checklist so the
// feedback appears while typing, not only on submit.
const displayedPasswordRules = computed(() => (
  mode.value === 'register'
    ? [...passwordRules.value, { label: 'Passwörter stimmen überein', valid: passwordsMatch.value }]
    : passwordRules.value
))

// Rules apply when choosing a NEW password (register / reset). Login must not
// be blocked by them: existing passwords only need to be sent to the backend.
const passwordRulesApply = computed(() => mode.value === 'register' || mode.value === 'reset-confirm')
const showPasswordRules = computed(() => passwordRulesApply.value)

const passwordLabel = computed(() => {
  switch (mode.value) {
    case 'register': return 'Passwort wählen'
    case 'reset-confirm': return 'Neues Passwort'
    default: return 'Passwort'
  }
})

// Display errors from the central Google login callback.
watch(googleAuthError, (message) => {
  if (message) {
    errorMsg.value = ERROR_MESSAGES[message] || 'Die Anmeldung mit Google ist fehlgeschlagen. Bitte versuche es erneut.'
    log('ui_error_shown', { reason: 'google', source: message.slice(0, 80) })
  }
})

const submitLabel = computed(() => {
  switch (mode.value) {
    case 'register': return 'Konto erstellen'
    case 'confirm': return 'Bestätigen'
    case 'reset': return 'Code senden'
    case 'reset-confirm': return 'Neues Passwort speichern'
    default: return 'Anmelden'
  }
})

function switchMode (next: Mode) {
  const from = mode.value
  mode.value = next
  errorMsg.value = ''
  infoMsg.value = ''
  if (from !== next) {
    log('tab_switch', { from, to: next })
  }
  if (next === 'login' || next === 'reset') {
    code.value = ''
    password.value = ''
    passwordConfirm.value = ''
  }
  // login → register keeps the typed password (common flow: login fails
  // because no account exists yet, user switches to the register tab);
  // the confirmation field always starts empty.
  if (next === 'register') {
    code.value = ''
    passwordConfirm.value = ''
  }
}

// Backend returns intentionally neutral English messages. Map to German
// user-facing text; unknown messages fall back to German to avoid English output.
const ERROR_MESSAGES: Record<string, string> = {
  'Invalid email or password': 'E-Mail-Adresse oder Passwort ist nicht korrekt. Bitte überprüfe deine Eingaben.',
  'Account already exists': 'Zu dieser E-Mail-Adresse existiert bereits ein Konto. Bitte melde dich an.',
  'Too many requests': 'Zu viele Versuche. Bitte warte einen Moment und versuche es dann erneut.',
  'Password reset required': 'Aus Sicherheitsgründen musst du dein Passwort neu festlegen. Bitte nutze „Passwort vergessen?“.',
  'Password does not meet the requirements': 'Das Passwort erfüllt nicht die Anforderungen. Bitte wähle mindestens 8 Zeichen mit Groß- und Kleinbuchstaben sowie einer Zahl.',
  'Invalid or expired code': 'Der eingegebene Code ist ungültig oder abgelaufen. Bitte fordere einen neuen Code an.',
  'Invalid email': 'Bitte gib eine gültige E-Mail-Adresse ein.',
  'Missing password': 'Bitte gib dein Passwort ein.',
  'Missing code': 'Bitte gib den Code aus der E-Mail ein.',
  'Missing new password': 'Bitte gib ein neues Passwort ein.',
  'Email login is currently unavailable': 'Die Anmeldung ist zurzeit leider nicht möglich. Bitte versuche es später erneut.',
  'Sign up failed': 'Die Registrierung ist leider fehlgeschlagen. Bitte versuche es später erneut.',
  'Confirmation failed': 'Die Bestätigung ist leider fehlgeschlagen. Bitte versuche es später erneut.',
  'Password reset failed': 'Das Passwort konnte nicht geändert werden. Bitte versuche es später erneut.',
  'Use Google sign-in': 'Dieses Konto ist mit Google registriert. Bitte melde dich mit Google an.'
}

// Raw backend message (untranslated) — used for flow decisions like
// "account already exists" which switch the view instead of showing an error.
function getRawMessage (e: unknown): string {
  return (e as { data?: { message?: string } })?.data?.message ?? ''
}

// Extract error message from backend response (ofetch puts body in err.data)
// and translate to German via ERROR_MESSAGES map.
function getErrorMessage (e: unknown, fallback: string): string {
  const message = (e as { data?: { message?: string } })?.data?.message
  return (message && ERROR_MESSAGES[message]) || fallback
}

async function onSubmit () {
  errorMsg.value = ''
  infoMsg.value = ''

  // Login: never block on the rule checklist (existing passwords may predate
  // the rules) — only require a non-empty password.
  if (mode.value === 'login' && !password.value) {
    errorMsg.value = 'Bitte gib dein Passwort ein.'
    log('client_validation_fail', { reason: 'missing_password', tab: mode.value })
    return
  }

  // New passwords (register/reset) must satisfy the checklist, which is
  // visible in these modes. Give explicit feedback instead of failing silently.
  if (passwordRulesApply.value && !passwordValid.value) {
    errorMsg.value = 'Bitte erfülle alle Passwort-Anforderungen.'
    log('client_validation_fail', { reason: 'password_rules', tab: mode.value })
    return
  }

  if (mode.value === 'register' && password.value !== passwordConfirm.value) {
    errorMsg.value = 'Die Passwörter stimmen nicht überein.'
    log('client_validation_fail', { reason: 'password_mismatch', tab: mode.value })
    return
  }

  loading.value = true
  try {
    if (mode.value === 'login') {
      const result = await loginWithCognito(email.value.trim(), password.value)
      if (result === 'confirm_required' || result === 'confirm_required_no_code') {
        switchMode('confirm')
        infoMsg.value = 'Dein Konto ist noch nicht bestätigt. Bitte gib den Code aus deiner Bestätigungs-E-Mail ein oder fordere einen neuen an.'
      } else {
        emit('success')
      }
    } else if (mode.value === 'register') {
      const result = await loginWithCognito(email.value.trim(), password.value, true)
      if (result === 'confirm_required') {
        switchMode('confirm')
        infoMsg.value = 'Wir haben dir einen Bestätigungscode per E-Mail gesendet. Bitte gib ihn hier ein. Manchmal landet die E-Mail im Spam-Ordner — schau bitte auch dort nach.'
      } else if (result === 'confirm_required_no_code') {
        // Account exists but is unconfirmed and the resend failed: don't
        // promise a mail that is not coming.
        switchMode('confirm')
        infoMsg.value = 'Zu dieser E-Mail-Adresse existiert bereits ein unbestätigtes Konto. Wir konnten gerade keinen neuen Code senden - bitte fordere über „Code erneut senden" einen neuen an.'
      } else {
        // Account existed and the credentials matched -> already logged in.
        emit('success')
      }
    } else if (mode.value === 'confirm') {
      const result = await confirmSignUp(email.value.trim(), code.value.trim())
      if (result === 'already_confirmed') {
        switchMode('login')
        infoMsg.value = 'Dein Konto ist bereits bestätigt. Bitte melde dich mit deinem Passwort an.'
      } else {
        emit('success')
      }
    } else if (mode.value === 'reset') {
      const result = await sendPasswordResetCode(email.value.trim())
      if (result === 'confirm_required') {
        // Unconfirmed account: no reset possible yet — a confirmation code
        // was sent instead, the account must be confirmed first.
        switchMode('confirm')
        infoMsg.value = 'Dein Konto ist noch nicht bestätigt. Wir haben dir einen Bestätigungscode gesendet — bitte bestätige zuerst dein Konto.'
      } else if (result === 'confirm_required_no_code') {
        // Confirmation required, but the code resend failed: point to the
        // explicit resend action instead of promising a mail.
        switchMode('confirm')
        infoMsg.value = 'Dein Konto ist noch nicht bestätigt. Wir konnten gerade keinen neuen Code senden - bitte fordere über „Code erneut senden" einen neuen an.'
      } else if (result === 'google_only') {
        // No Cognito password exists. Stay on login; do not open the code field.
        switchMode('login')
        errorMsg.value = ERROR_MESSAGES['Use Google sign-in']
        log('ui_error_shown', { reason: 'google_only', tab: 'reset' })
      } else {
        switchMode('reset-confirm')
        infoMsg.value = 'Falls ein Konto mit dieser E-Mail-Adresse existiert, haben wir dir einen Code gesendet.'
      }
    } else if (mode.value === 'reset-confirm') {
      await confirmPasswordReset(email.value.trim(), code.value.trim(), password.value)
      switchMode('login')
      infoMsg.value = 'Dein Passwort wurde geändert. Bitte melde dich mit deinem neuen Passwort an.'
    }
  } catch (e) {
    // Registration against an existing account: not an error — route to the
    // login tab with a friendly hint (email stays filled in, password cleared).
    if (mode.value === 'register' && getRawMessage(e) === 'Account already exists') {
      switchMode('login')
      infoMsg.value = 'Zu dieser E-Mail-Adresse existiert bereits ein Konto. Bitte melde dich an oder nutze „Passwort vergessen?“.'
    } else {
      errorMsg.value = getErrorMessage(e, 'Es ist ein Fehler aufgetreten. Bitte versuche es später erneut.')
      log('ui_error_shown', { reason: getRawMessage(e).slice(0, 80) || 'unknown', tab: mode.value })
    }
  } finally {
    loading.value = false
  }
}

async function onResendCode () {
  errorMsg.value = ''
  infoMsg.value = ''
  loading.value = true
  try {
    const result = await resendSignUpConfirmationCode(email.value.trim())
    if (result === 'already_confirmed') {
      switchMode('login')
      infoMsg.value = 'Dein Konto ist bereits bestätigt. Bitte melde dich mit deinem Passwort an.'
    } else {
      infoMsg.value = 'Wir haben dir einen neuen Code gesendet.'
    }
  } catch (e) {
    errorMsg.value = getErrorMessage(e, 'Der Code konnte nicht gesendet werden. Bitte versuche es später erneut.')
    log('ui_error_shown', { reason: getRawMessage(e).slice(0, 80) || 'resend_failed', tab: mode.value })
  } finally {
    loading.value = false
  }
}
</script>
